import React, { useState, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { BsPencilSquare, BsTrash } from 'react-icons/bs';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { globalErrorHandler } from '../../../utils/GlobalErrorHandler'


const Properties = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [states, setStates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState({
    property_name: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip: '',
    google_map_location: ''
  });

  const openAddModal = () => {
    setIsEdit(false);
    setForm({
      property_name: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      zip: '',
      google_map_location: ''
    });
    setShowModal(true);
  };

  const openEditModal = (property) => {
    setIsEdit(true);
    setForm({ ...property });
    setShowModal(true);
  };

  const getProperties = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(process.env.REACT_APP_WEB_SERVICES_URL + "/admin/get-properties", {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      });
      if (response?.data?.status) {
        setProperties(response.data.properties || []);
      } else {
        Swal.fire({ icon: "error", title: "Error", text: "No Record Found" });
      }
    } catch (error) {
      globalErrorHandler(error, navigate); 
        }
    setIsLoading(false);
  };



  const getStates = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(process.env.REACT_APP_WEB_SERVICES_URL + "/get-states", {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      });
      if (response?.data?.status) {
        setStates(response.data.states || []);
      } else {
        Swal.fire({ icon: "error", title: "Error", text: "No States Record Found" });
      }
    } catch (error) {
      globalErrorHandler(error, navigate);     }
    setIsLoading(false);
  };


  const validatePropertyForm = () => {
    const requiredFields = [
      'property_name',
      'address_line_1',
      
    ];

    const missingFields = requiredFields.filter(field => !form[field]?.trim());

    if (missingFields.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: `Please fill in the required fields: ${missingFields.join(', ').replaceAll('_', ' ')}`
      });
      return false;
    }

    return true;
  };

  const saveProperty = async () => {
    if (!validatePropertyForm()) return;

    try {
      await axios.post(process.env.REACT_APP_WEB_SERVICES_URL + "/admin/create-property", form, {
        withCredentials: true
      });
      Swal.fire('Success', 'Property added!', 'success');
      getProperties();
      setShowModal(false);
    } catch (error) {
      globalErrorHandler(error, navigate);     }
  };

  const updateProperty = async () => {
    if (!validatePropertyForm()) return;

    try {
      await axios.post(process.env.REACT_APP_WEB_SERVICES_URL + "/admin/update-property", form, {
        withCredentials: true
      });
      Swal.fire('Success', 'Property updated!', 'success');
      getProperties();
      setShowModal(false);
    } catch (error) {
      globalErrorHandler(error, navigate);     }
  };


  const deleteProperty = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete the property.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.post(process.env.REACT_APP_WEB_SERVICES_URL + "/admin/delete-property", { id }, {
            withCredentials: true
          });
          Swal.fire('Deleted', 'Property has been deleted.', 'success');
          getProperties();
        } catch (error) {
      globalErrorHandler(error, navigate);         }
      }
    });
  };

  useEffect(() => {
    getProperties();
    getStates();
  }, []);

  return (
    <div className="container-fluid px-4 mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">🏠 Properties</h3>
        <Button variant="success" onClick={openAddModal}>
          + Add New Property
        </Button>
      </div>

      <div className="table-responsive">
      <table className="table table-bordered table-hover shadow-sm">
          <thead className="table-success">
            <tr>
              <th>Property Name</th>
              <th>Address</th>
              <th>City</th>
              <th>State</th>
              <th>ZIP</th>
              <th>Map Location</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan="7" className="text-center py-3">Loading...</td></tr>
            ) : properties.length ? (
              properties.map((prop) => (
                <tr key={prop.id}>
                  <td>{prop.property_name}</td>
                  <td>{prop.address_line_1} {prop.address_line_2}</td>
                  <td>{prop.city}</td>
                  <td>{prop.state}</td>
                  <td>{prop.zip}</td>
                  <td>{prop.google_map_location}</td>
                  <td className="text-center">
                    <Button variant="outline-primary" size="sm" className="me-2" onClick={() => openEditModal(prop)}>
                      <BsPencilSquare />
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => deleteProperty(prop.id)}>
                      <BsTrash />
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="7" className="text-center py-3">No properties found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="md" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? "Edit Property" : "Add Property"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-2">
            <div className="col-md-12">
              <label className="form-label">Property Name</label>
              <input type="text" className="form-control"
                value={form.property_name}
                onChange={(e) => setForm({ ...form, property_name: e.target.value })} />
            </div>
            <div className="col-md-12">
              <label className="form-label">Address Line 1</label>
              <input type="text" className="form-control"
                value={form.address_line_1}
                onChange={(e) => setForm({ ...form, address_line_1: e.target.value })} />
            </div>
            <div className="col-md-12">
              <label className="form-label">Address Line 2</label>
              <input type="text" className="form-control"
                value={form.address_line_2}
                onChange={(e) => setForm({ ...form, address_line_2: e.target.value })} />
            </div>
            <div className="col-md-4">
              <label className="form-label">City</label>
              <input type="text" className="form-control"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div className="col-md-5">
              <label className="form-label">State</label>
              <select className="form-select" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
              >
                <option value="">Select State</option>
                {states.map((state) => (
                  <option key={state.state_id} value={state.state_abbreviation}>{state.state_name} ({state.state_abbreviation})</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">ZIP</label>
              <input type="text" className="form-control"
                value={form.zip}
                onChange={(e) => setForm({ ...form, zip: e.target.value })} />
            </div>
            <div className="col-12">
              <label className="form-label">Google Map Location</label>
              <input type="text" className="form-control"
                value={form.google_map_location}
                onChange={(e) => setForm({ ...form, google_map_location: e.target.value })} />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={isEdit ? updateProperty : saveProperty}>
            {isEdit ? 'Update Property' : 'Save Property'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Properties;
